"use client";

import { useEffect, useRef } from "react";

const ACTIVE_POPUP_SELECTOR = [
  ".MuiModal-root:not([aria-hidden='true'])",
  ".MuiPopover-root:not([aria-hidden='true'])",
  "[role='dialog'][aria-modal='true']",
].join(",");

const POPUP_SCROLL_AREA_SELECTOR = [
  ".MuiDialog-paper",
  ".MuiDialogContent-root",
  ".MuiPopover-paper",
  ".MuiMenu-paper",
  ".MuiDrawer-paper",
  "[data-popup-scroll-area='true']",
].join(",");

function hasVisibleActivePopup() {
  if (typeof window === "undefined") {
    return false;
  }

  return Array.from(document.body.querySelectorAll<HTMLElement>(ACTIVE_POPUP_SELECTOR)).some((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}

function emitScrollLockChange(locked: boolean) {
  window.dispatchEvent(new CustomEvent("app-popup-scroll-lock", { detail: { locked } }));
}

export function PopupScrollLock() {
  const isLockedRef = useRef(false);

  useEffect(() => {
    let frameId: number | null = null;

    const applyLock = () => {
      const shouldLock = hasVisibleActivePopup();

      document.documentElement.classList.toggle("app-popup-scroll-locked", shouldLock);
      document.body.classList.toggle("app-popup-scroll-locked", shouldLock);

      if (isLockedRef.current !== shouldLock) {
        isLockedRef.current = shouldLock;
        emitScrollLockChange(shouldLock);
      }
    };

    const scheduleApplyLock = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        applyLock();
      });
    };

    const preventBackgroundScroll = (event: WheelEvent | TouchEvent) => {
      if (!isLockedRef.current) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;

      if (target?.closest(POPUP_SCROLL_AREA_SELECTOR)) {
        return;
      }

      event.preventDefault();
    };

    applyLock();

    const observer = new MutationObserver(scheduleApplyLock);
    observer.observe(document.body, {
      attributeFilter: ["aria-hidden", "class", "style"],
      attributes: true,
      childList: true,
      subtree: true,
    });

    document.addEventListener("wheel", preventBackgroundScroll, { capture: true, passive: false });
    document.addEventListener("touchmove", preventBackgroundScroll, { capture: true, passive: false });
    window.addEventListener("resize", scheduleApplyLock);

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      observer.disconnect();
      document.removeEventListener("wheel", preventBackgroundScroll, { capture: true });
      document.removeEventListener("touchmove", preventBackgroundScroll, { capture: true });
      window.removeEventListener("resize", scheduleApplyLock);
      document.documentElement.classList.remove("app-popup-scroll-locked");
      document.body.classList.remove("app-popup-scroll-locked");

      if (isLockedRef.current) {
        emitScrollLockChange(false);
      }
    };
  }, []);

  return null;
}
